import { Form, Input } from 'antd'
import { type FC, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useFieldArray } from 'react-hook-form'
import { FaPlus, FaTimes } from 'react-icons/fa'

import {
  BasicTable,
  BasicTbody,
  BasicTd,
  BasicTh,
  BasicTr,
  Button,
  type NormalizedInputNumberHandle,
  NormalizedInputNumber,
  NumberFormatter
} from '@/components/elements'
import { ACTIONS, LABELS } from '@/constants/common/message.constants'
import {
  PackageCountFormatter,
  PackageCountFormatValue
} from '@/features/shipment-order-details/components'

import { ShipmentOrderWeightInput } from '../ShipmentOrderWeightInput'

import {
  type ShipmentOrderCardItemFormValue,
  type ShipmentOrderCardNamePrefix
} from './shipment-order-card-form-values'
import { type ShipmentOrderFormCardMode } from './shipment-order-form-card-mode'
import { useShipmentOrderCardField } from './use-shipment-order-card-field'

type Props = {
  namePrefix?: ShipmentOrderCardNamePrefix
  /** create は既存明細が無いため、行追加時に detailId を持たせない */
  mode: ShipmentOrderFormCardMode
  canEditFields: boolean
  setNumericRef: (
    name: string
  ) => (el: NormalizedInputNumberHandle | null) => void
  /** 重量変更時に自動計算結果をリセットする（請求運賃の自動計算ボタン活性条件と連動） */
  onWeightChange: () => void
}

/** 明細行追加時の初期値。create は detailId を持たない（既存明細が存在しないため）。 */
const buildNewItem = (
  mode: ShipmentOrderFormCardMode
): ShipmentOrderCardItemFormValue =>
  mode === 'create'
    ? {
        itemCode: null,
        itemName: '',
        packagedLoadQuantity: null,
        packageCount: null,
        quantity: null,
        weight: null
      }
    : {
        detailId: -1,
        itemCode: null,
        itemName: '',
        packagedLoadQuantity: null,
        packageCount: null,
        quantity: null,
        weight: null
      }

/**
 * 配送依頼の明細（品目コード/品目名/荷姿単位数量/荷姿数/数量/重量）のテーブル。
 * 荷姿単位数量・荷姿数は「数量 ÷ 相手」の自動計算値を、手動入力されるまで表示する。
 */
export const ItemRows: FC<Props> = ({
  namePrefix,
  mode,
  canEditFields,
  setNumericRef,
  onWeightChange
}) => {
  const { name, watchField, form } = useShipmentOrderCardField(namePrefix)
  const { control, setError, clearErrors } = form

  const {
    fields: itemFields,
    append: appendItem,
    remove: removeItem
    // `useFieldArray` の name は namePrefix 込みの動的パスを `string` に narrow できないため cast する
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useFieldArray({ control, name: name('items') as any })

  const rawWatchedItems = watchField('items') as
    | ShipmentOrderCardItemFormValue[]
    | undefined
  const watchedItems = useMemo(() => rawWatchedItems ?? [], [rawWatchedItems])
  const quantities = useMemo(
    () => watchedItems.map((item) => item?.quantity),
    [watchedItems]
  )
  const packageCounts = useMemo(
    () => watchedItems.map((item) => item?.packageCount),
    [watchedItems]
  )
  const packagedLoadQuantities = useMemo(
    () => watchedItems.map((item) => item?.packagedLoadQuantity),
    [watchedItems]
  )

  useEffect(() => {
    // 各明細ごとに荷姿数が手動入力されたかどうか判定
    const newManualPackageCount = Object.fromEntries(
      (watchedItems ?? []).map((item, idx) => [
        idx,
        item?.packageCount !== null
      ])
    )
    // 各明細ごとに荷姿単位数量が手動入力されたかどうか判定
    const newManualPackagedLoadQuantity = Object.fromEntries(
      (watchedItems ?? []).map((item, idx) => [
        idx,
        item?.packagedLoadQuantity !== null
      ])
    )
    // 前回の状態と異なる場合のみmanualPackageCountを更新
    setManualPackageCount((prev) =>
      JSON.stringify(prev) !== JSON.stringify(newManualPackageCount)
        ? newManualPackageCount
        : prev
    )
    // 前回の状態と異なる場合のみmanualPackagedLoadQuantityを更新
    setManualPackagedLoadQuantity((prev) =>
      JSON.stringify(prev) !== JSON.stringify(newManualPackagedLoadQuantity)
        ? newManualPackagedLoadQuantity
        : prev
    )
  }, [watchedItems])

  // 明細ごとに手動入力フラグを管理
  const [manualPackageCount, setManualPackageCount] = useState<{
    [index: number]: boolean
  }>({})
  const [manualPackagedLoadQuantity, setManualPackagedLoadQuantity] = useState<{
    [index: number]: boolean
  }>({})

  // 荷姿数の自動計算値を配列で生成
  const autoPackageCounts = useMemo(
    () =>
      // 各明細ごとに 数量 と 荷姿単位数量 が入力済みかつ手動入力されていない場合のみ自動計算
      quantities.map((quantity, index) => {
        const packagedLoadQuantity = packagedLoadQuantities[index]
        if (
          quantity !== null &&
          quantity !== 0 &&
          packagedLoadQuantity !== null &&
          packagedLoadQuantity !== 0 &&
          !manualPackageCount[index]
        ) {
          return PackageCountFormatValue(quantity / packagedLoadQuantity)
        }
        return null
      }),
    [quantities, packagedLoadQuantities, manualPackageCount]
  )

  // 荷姿単位数量の自動計算値を配列で生成
  const autoPackagedLoadQuantities = useMemo(
    () =>
      // 各明細ごとに 数量 と 荷姿数 が入力済みかつ手動入力されていない場合のみ自動計算
      quantities.map((quantity, index) => {
        const packageCount = packageCounts[index]
        if (
          quantity !== null &&
          quantity !== 0 &&
          packageCount !== null &&
          packageCount !== 0 &&
          !manualPackagedLoadQuantity[index]
        ) {
          return PackageCountFormatValue(quantity / packageCount)
        }
        return null
      }),
    [quantities, packageCounts, manualPackagedLoadQuantity]
  )

  const didInitRef = useRef(false)
  useEffect(() => {
    if (didInitRef.current) return

    if (
      canEditFields &&
      itemFields.length === 0 &&
      (rawWatchedItems?.length ?? 0) === 0
    ) {
      appendItem(buildNewItem(mode), { shouldFocus: false })
    }

    didInitRef.current = true
  }, [
    didInitRef,
    itemFields.length,
    rawWatchedItems?.length,
    canEditFields,
    appendItem,
    mode
  ])

  return (
    <BasicTable>
      <BasicTbody>
        <BasicTr>
          <BasicTh style={{ color: 'black' }}>
            {LABELS.ITEM_INFORMATION}
          </BasicTh>
        </BasicTr>
        <BasicTr>
          <BasicTh>{LABELS.ITEM_CODE}</BasicTh>
          <BasicTh>{LABELS.ITEM_NAME}</BasicTh>
          <BasicTh>{LABELS.PACKAGED_LOAD_QUANTITY}</BasicTh>
          <BasicTh>{LABELS.PACKAGE_QUANTITY}</BasicTh>
          <BasicTh>{LABELS.QUANTITY}</BasicTh>
          <BasicTh>{LABELS.WEIGHT_KG}</BasicTh>
          <BasicTh />
        </BasicTr>
        {itemFields.map((field, index) => (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          <BasicTr key={(field as any).id}>
            {/* 品目コード */}
            <BasicTd className="text-center">
              {canEditFields ? (
                <Controller
                  name={name(`items.${index}.itemCode`)}
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder={LABELS.ITEM_CODE}
                      value={field.value ?? ''}
                    />
                  )}
                />
              ) : (
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (field as any).itemCode || '-'
              )}
            </BasicTd>
            {/* 品目名 */}
            <BasicTd className="text-center">
              {canEditFields ? (
                <Controller
                  name={name(`items.${index}.itemName`)}
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder={LABELS.ITEM_NAME}
                      value={field.value ?? ''}
                    />
                  )}
                />
              ) : (
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (field as any).itemName || '-'
              )}
            </BasicTd>
            {/* 荷姿単位数量 */}
            <BasicTd className="text-center">
              {canEditFields ? (
                <Controller
                  name={name(`items.${index}.packagedLoadQuantity`)}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <NormalizedInputNumber
                        ref={setNumericRef(
                          name(`items.${index}.packagedLoadQuantity`)
                        )}
                        id={field.name}
                        value={
                          manualPackagedLoadQuantity[index]
                            ? (field.value ?? null)
                            : (autoPackagedLoadQuantities[index] ?? null)
                        }
                        onChange={(val) => {
                          field.onChange(val === undefined ? null : val)
                          setManualPackagedLoadQuantity((prev) => ({
                            ...prev,
                            [index]: val !== null && val !== undefined
                          }))
                        }}
                        onBlur={() => {
                          if (
                            field.value === null ||
                            field.value === undefined
                          ) {
                            setManualPackagedLoadQuantity((prev) => ({
                              ...prev,
                              [index]: false
                            }))
                          }
                        }}
                        onInvalidValue={(msg) =>
                          setError(
                            name(`items.${index}.packagedLoadQuantity`),
                            {
                              message: msg
                            },
                            { shouldFocus: true }
                          )
                        }
                        onValid={() =>
                          clearErrors(
                            name(`items.${index}.packagedLoadQuantity`)
                          )
                        }
                        placeholder="0"
                        className="w-full text-right"
                      />
                    </Form.Item>
                  )}
                />
              ) : (
                <PackageCountFormatter
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  value={(field as any).packagedLoadQuantity}
                />
              )}
            </BasicTd>
            {/* 荷姿数 */}
            <BasicTd className="text-center">
              {canEditFields ? (
                <Controller
                  name={name(`items.${index}.packageCount`)}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <NormalizedInputNumber
                        ref={setNumericRef(name(`items.${index}.packageCount`))}
                        id={field.name}
                        value={
                          manualPackageCount[index]
                            ? (field.value ?? null)
                            : (autoPackageCounts[index] ?? null)
                        }
                        onChange={(val) => {
                          field.onChange(val === undefined ? null : val)
                          setManualPackageCount((prev) => ({
                            ...prev,
                            [index]: val !== null && val !== undefined
                          }))
                        }}
                        onBlur={() => {
                          if (
                            field.value === null ||
                            field.value === undefined
                          ) {
                            setManualPackageCount((prev) => ({
                              ...prev,
                              [index]: false
                            }))
                          }
                        }}
                        onInvalidValue={(msg) =>
                          setError(
                            name(`items.${index}.packageCount`),
                            {
                              message: msg
                            },
                            { shouldFocus: true }
                          )
                        }
                        onValid={() =>
                          clearErrors(name(`items.${index}.packageCount`))
                        }
                        placeholder="0"
                        className="w-full text-right"
                      />
                    </Form.Item>
                  )}
                />
              ) : (
                <PackageCountFormatter
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  value={(field as any).packageCount}
                />
              )}
            </BasicTd>
            {/* 数量 */}
            <BasicTd className="text-center">
              {canEditFields ? (
                <Controller
                  name={name(`items.${index}.quantity`)}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <NormalizedInputNumber
                        ref={setNumericRef(name(`items.${index}.quantity`))}
                        id={field.name}
                        value={field.value ?? null}
                        onChange={(val) => field.onChange(val ?? null)}
                        onInvalidValue={(msg) =>
                          setError(
                            name(`items.${index}.quantity`),
                            {
                              message: msg
                            },
                            { shouldFocus: true }
                          )
                        }
                        onValid={() =>
                          clearErrors(name(`items.${index}.quantity`))
                        }
                        placeholder="0"
                        className="w-full text-right"
                      />
                    </Form.Item>
                  )}
                />
              ) : (
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                <NumberFormatter value={(field as any).quantity} />
              )}
            </BasicTd>
            {/* 重量(t) */}
            <BasicTd className="text-center">
              {canEditFields ? (
                <Controller
                  name={name(`items.${index}.weight`)}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <ShipmentOrderWeightInput
                        {...field}
                        ref={setNumericRef(name(`items.${index}.weight`))}
                        id={field.name}
                        className="w-full text-right"
                        placeholder="0"
                        onInvalidValue={(msg) =>
                          setError(
                            name(`items.${index}.weight`),
                            {
                              message: msg
                            },
                            { shouldFocus: true }
                          )
                        }
                        onValid={() => clearErrors(field.name)}
                        onChange={(value) => {
                          field.onChange(value)
                          onWeightChange()
                        }}
                      />
                    </Form.Item>
                  )}
                />
              ) : (
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                <NumberFormatter value={(field as any).weight} />
              )}
            </BasicTd>
            {/* 削除ボタン */}
            <BasicTd>
              {canEditFields && (
                <Button
                  type="text"
                  icon={<FaTimes />}
                  style={{ color: '#888' }}
                  disabled={itemFields.length === 1}
                  onClick={() => {
                    if (itemFields.length > 1) {
                      removeItem(index)
                    }
                  }}
                />
              )}
            </BasicTd>
          </BasicTr>
        ))}
        {/* 明細追加ボタン */}
        {canEditFields && (
          <BasicTr>
            <BasicTd colSpan={7}>
              <div className="text-right">
                <Button
                  type="link"
                  icon={<FaPlus />}
                  onClick={() => {
                    // 新規追加行は更新系モードでは存在しないID-1を設定（create は detailId を持たない）
                    appendItem(buildNewItem(mode))
                  }}
                >
                  {ACTIONS.ADD_ITEM}
                </Button>
              </div>
            </BasicTd>
          </BasicTr>
        )}
      </BasicTbody>
    </BasicTable>
  )
}
